import { DepartmentDTO, ProjectDTO2, UserDTO } from '../../../../../api/api';
import { SectionProps } from '../../../../../models/CommonProps/SectionProps';
import { useCallback, useEffect, useState } from 'react';
import ProjectsService from '../../../../../services/ProjectsService';
import styled from 'styled-components';
import TextField from '../../../../../components/text-field/TextField';
import Checkbox from '../../../../../components/checkbox/Checkbox';
import { useDispatch } from 'react-redux';
import { updateTask } from '../../../../../stateManagement/reducers/taskCreationReducer';
import { TaskCreationTask } from '../../../../../models/TaskCreationTask';
import { addBusinessAndHolidays, changeToEndOfDay, formatDateString } from '../../../../../utils/dateHandling';
import DepartmentDropdown from '../../../../../components/department-dropdown/DepartmentDropdown';
import { log } from '../../../../../utils/logging/log';
import DatePicker from '../../../../../components/date-picker/DatePicker';
import { SectionContent, Row } from '../../../../../styling/FormStyling';
import AutoComplete from '../../../../../components/auto-complete/AutoComplete';
import { updateMassCreationTask } from '../../../../../stateManagement/reducers/massCreationReducer';
import { MassCreationTask } from '../../../../../models/MassCreationTask';
import { convertHoursAndMinutesToMinutes, convertMinutesToHoursAndMinutes } from '../../../../../utils/timeHandling';
import SimpleTime from '../../../../../components/simple-time/SimpleTime';
import { getWorkPermitTaskTypeId, PLANNING_DEPARTMENT_ID } from '../../../../../utils/constants';
import DateTimePicker from '../../../../../components/date-time-picker/DateTimePicker';
import dayjs from 'dayjs';
import Alert from '../../../../../components/alert/Alert';
import UserService from '../../../../../services/UserService';
import { getUserEmail } from '../../../../../utils/authProvider/authProvider';
import NotificationService from '../../../../../services/NotificationService';
import IconButton from '../../../../../components/icon-button/IconButton';
import { DownloadIcon2 } from '../../../../../assets/icons/DownloadIcon2';
import n1LightTheme from '../../../../../theme/light-theme';
import ToolTip from '../../../../../components/tool-tip/ToolTip';
import CircularProgress from '../../../../../components/circular-progress/CircularProgress';
import { getPreparationTaskDates } from '../../../../../utils/preparationTaskDates';

interface MappedProjectNumber {
  label: string;
  value: ProjectDTO2;
}

interface Props extends SectionProps {
  task: TaskCreationTask | MassCreationTask;
  isMassCreation?: boolean;
  disableDates?: boolean;
}

const DetailsSection = (props: Props) => {
  const { task, readonlyMode, isMassCreation, disableDates = false } = props;

  const {
    assignedToDepartmentId = PLANNING_DEPARTMENT_ID,
    deadline = changeToEndOfDay(new Date()).toISOString(),
    earliestStartDate,
    projectNumber,
    normTimeMin = 0,
    baseErrors
  } = task;

  const {
    taskCreationId = '',
    normTimeValidated = false,
    description,
    parentEarliestStartDate,
    parentDeadline
  } = task as TaskCreationTask;

  const [localNormTime, setLocalNormTime] = useState(normTimeMin);
  const [projectNumbers, setProjectNumbers] = useState<ProjectDTO2[]>([]);
  const [selectedProjectNumber, setSelectedProjectNumber] = useState<MappedProjectNumber | null>(null);
  const [workTaskResponsible, setWorkTaskResponsible] = useState<UserDTO>();
  const [descriptionValue, setDescriptionValue] = useState(description);
  const [isLoadingProjectNumbers, setIsLoadingProjectNumbers] = useState(true);
  const [users, setUsers] = useState<UserDTO[]>([]);
  const [isFetchingProjectNumber, setIsFetchingProjectNumber] = useState(false);

  useEffect(() => {
    UserService.getUser().then((user) => {
      const name = user.name;
      const email = getUserEmail();
      handleWorkTaskResponsibleChange({ name: name, email: email });
    });
  }, []);

  useEffect(() => {
    UserService.getAllKnownUsers()
      .then((users) => {
        setUsers(users.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')));
      })
      .catch((e) => {
        NotificationService.error(e);
      });
  }, []);

  const dispatch = useDispatch();

  const projectNumberToOption = useCallback((p: ProjectDTO2): MappedProjectNumber => {
    return {
      label: `${p.id} - ${p.name} (${p.legalEntityId})`,
      value: p
    };
  }, []);

  const mappedProjectNumbers = projectNumbers.map((p) => projectNumberToOption(p));

  const isWorkPermit = task.taskType?.id === getWorkPermitTaskTypeId();

  const handleStartDateChange = useCallback(
    (date: string | null | undefined) => {
      if (date) {
        const newDeadline = addBusinessAndHolidays(
          new Date(date),
          task?.taskType?.slaDays && task?.taskType.slaDays > 0 ? task?.taskType?.slaDays : 0
        ).toISOString();
        const newStartDate = new Date(date).toISOString();
        const inheritedPreparationDeadline = task.taskType?.isPreparationTask ? parentDeadline ?? task.deadline : undefined;

        // A work permit's end time is set by the user, so a new start time must not move it
        const updatedTask = isWorkPermit
          ? { earliestStartDate: newStartDate }
          : { earliestStartDate: newStartDate, deadline: inheritedPreparationDeadline ?? newDeadline };

        if (isMassCreation) {
          dispatch(updateMassCreationTask(updatedTask));
        } else {
          dispatch(
            updateTask({
              id: taskCreationId,
              task: updatedTask
            })
          );
        }
      }
    },
    [
      dispatch,
      isMassCreation,
      isWorkPermit,
      parentDeadline,
      task.deadline,
      task?.taskType?.isPreparationTask,
      task?.taskType?.slaDays,
      taskCreationId
    ]
  );

  const handleDeadlineDateChange = useCallback(
    (date: string | null | undefined) => {
      if (date) {
        const newDeadline = isWorkPermit ? date : changeToEndOfDay(new Date(date)).toISOString();
        if (isMassCreation) {
          dispatch(updateMassCreationTask({ deadline: newDeadline }));
        } else {
          dispatch(updateTask({ id: taskCreationId, task: { deadline: newDeadline } }));
        }
      }
    },
    [dispatch, isMassCreation, isWorkPermit, taskCreationId]
  );

  const handleFollowUpDateChange = useCallback(
    (date: string | null | undefined) => {
      if (date) {
        const newFollowUpDate = changeToEndOfDay(new Date(date)).toISOString();
        dispatch(updateTask({ id: taskCreationId, task: { followUpDate: newFollowUpDate } }));
      }
    },
    [dispatch, taskCreationId]
  );

  const updateProjectNumber = useCallback(
    (project: { label: string; value: ProjectDTO2 }) => {
      setSelectedProjectNumber(project);

      const updatedTask = {
        projectNumber: project?.value.id,
        projectLegalEntityId: project?.value.legalEntityId,
        region: task.gisRegion
      };

      if (isMassCreation) {
        dispatch(updateMassCreationTask(updatedTask));
      } else {
        dispatch(
          updateTask({
            id: taskCreationId,
            task: updatedTask
          })
        );
      }
    },
    [dispatch, isMassCreation, task, taskCreationId]
  );

  const handleWorkTaskResponsibleChange = (value: UserDTO | null) => {
    if (!value?.email || !value?.name) {
      return;
    }
    setWorkTaskResponsible({
      name: value.name,
      email: value.email
    });
    if (isMassCreation) {
      dispatch(
        updateMassCreationTask({
          workTaskResponsibleName: value.name,
          workTaskResponsibleEmail: value.email
        })
      );
    } else {
      dispatch(
        updateTask({
          id: taskCreationId,
          task: {
            workTaskResponsibleName: value.name,
            workTaskResponsibleEmail: value.email
          }
        })
      );
    }
  };

  const handleProjectNumberChange = useCallback(
    (_, value: string | undefined, reason) => {
      if (reason === 'clear') {
        setSelectedProjectNumber(null);
      }
      const project = mappedProjectNumbers.find((project) => project.value.id + project.value.legalEntityId === value);
      project && updateProjectNumber(project);
    },
    [mappedProjectNumbers, updateProjectNumber]
  );

  const handleOnChangeSendTo = (assignedToDepartment: DepartmentDTO) => {
    if (isMassCreation) {
      dispatch(updateMassCreationTask({ assignedToDepartmentId: assignedToDepartment.departmentId }));
    } else {
      dispatch(updateTask({ id: taskCreationId, task: { assignedToDepartmentId: assignedToDepartment.departmentId } }));
    }
  };

  const handleNormTimeValidatedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isMassCreation) {
      dispatch(updateMassCreationTask({ normTimeValidated: e.target.checked }));
    } else {
      dispatch(updateTask({ id: taskCreationId, task: { normTimeValidated: e.target.checked } }));
    }
  };

  const handleNormTimeBlur = useCallback(
    (normTime: number) => {
      if (isMassCreation) {
        dispatch(updateMassCreationTask({ normTimeMin: normTime }));
      } else {
        dispatch(updateTask({ id: taskCreationId, task: { normTimeMin: normTime } }));
      }
    },
    [dispatch, isMassCreation, taskCreationId]
  );

  const handleDescriptionBlur = useCallback(() => {
    dispatch(updateTask({ id: taskCreationId, task: { description: descriptionValue } }));
  }, [descriptionValue, dispatch, taskCreationId]);

  const handlePreparationDaysChange = useCallback(
    (field: 'minTimeBeforeMainTaskDays' | 'maxTimeBeforeMainTaskDays', value: string) => {
      const days = value === '' ? undefined : Number(value);
      const updatedTask: TaskCreationTask = { [field]: days };

      if (field === 'maxTimeBeforeMainTaskDays') {
        Object.assign(updatedTask, getPreparationTaskDates(parentEarliestStartDate, parentDeadline, days));
      }

      dispatch(
        updateTask({
          id: taskCreationId,
          task: updatedTask
        })
      );
    },
    [dispatch, parentDeadline, parentEarliestStartDate, taskCreationId]
  );

  // A preparation task type without a day window leaves the days optional, see validateBaseFields.
  const taskTypeHasDayWindow =
    task.taskType?.minTimeBeforeMainTaskDays != null || task.taskType?.maxTimeBeforeMainTaskDays != null;

  const getPreparationMinHelperText = () => {
    if (!baseErrors?.minTimeBeforeMainTaskDays) return undefined;
    if (!taskTypeHasDayWindow)
      return 'Angiv hele dage (0 eller mere) i både min. og maks., og min. må ikke være større end maks.';
    const taskTypeMin = task.taskType?.minTimeBeforeMainTaskDays ?? 0;
    const taskTypeMax = task.taskType?.maxTimeBeforeMainTaskDays ?? 0;
    return `Angiv hele dage mellem ${taskTypeMin} og ${taskTypeMax}, og min. må ikke være større end maks.`;
  };

  const getPreparationMaxHelperText = () => {
    if (!baseErrors?.maxTimeBeforeMainTaskDays) return undefined;
    if (!taskTypeHasDayWindow)
      return 'Angiv hele dage (0 eller mere) i både min. og maks., og maks. må ikke være mindre end min.';
    const taskTypeMin = task.taskType?.minTimeBeforeMainTaskDays ?? 0;
    const taskTypeMax = task.taskType?.maxTimeBeforeMainTaskDays ?? 0;
    return `Angiv hele dage mellem ${taskTypeMin} og ${taskTypeMax}, og maks. må ikke være mindre end min.`;
  };

  useEffect(() => {
    if (earliestStartDate === undefined) {
      handleStartDateChange && handleStartDateChange(new Date().toISOString());
    }
  }, [earliestStartDate, handleStartDateChange]);

  useEffect(() => {
    if (projectNumbers.length === 0) {
      ProjectsService.getProjectsByCategory(undefined)
        .then((res) => {
          if (task.projectNumber) {
            const selected = res.find((p) => p.id === projectNumber);
            if (selected) {
              updateProjectNumber(projectNumberToOption(selected));
            }
          }
          setProjectNumbers(res);
        })
        .catch((err) => {
          log(err);
        })
        .finally(() => {
          setIsLoadingProjectNumbers(false);
        });
    }
  }, [projectNumber, projectNumberToOption, projectNumbers.length, task?.projectNumber, updateProjectNumber]);

  const selectDepartment = (d: DepartmentDTO) => {
    handleOnChangeSendTo(d);
  };

  const timeChangeHandler = useCallback((hours, minutes) => {
    setLocalNormTime(convertHoursAndMinutesToMinutes(hours, minutes));
  }, []);

  const getProjectNumberFromWorkTask = () => {
    if ('parentId' in task && task.parentId) {
      setIsFetchingProjectNumber(true);
      ProjectsService.getProjectByWorkTaskId(task.parentId)
        .then((response) => {
          setSelectedProjectNumber(projectNumberToOption(response));
        })
        .catch((e) => {
          log(e);
          NotificationService.error('Kunne ikke hente projektnummer fra hovedopgaven.');
        })
        .finally(() => {
          setIsFetchingProjectNumber(false);
        });
    }
  };

  return (
    <SectionContent>
      {isWorkPermit ? (
        <Alert severity="action" padding="12px">
          Bemærk dette er en arbejdstilladelse. Det ligner en opgave, men opfører sig anderledes. Det kan tænkes som et
          dokument/ansøgning som sendes til kontrolrummet
        </Alert>
      ) : (
        <Row>
          <RowItemContainer>
            <SimpleTime
              initialHours={convertMinutesToHoursAndMinutes(normTimeMin).hours}
              initialMinutes={convertMinutesToHoursAndMinutes(normTimeMin).minutes}
              onTimeChange={(hours, minutes) => timeChangeHandler(hours, minutes)}
              onBlur={() => handleNormTimeBlur(localNormTime)}
            />
          </RowItemContainer>
          <RowItemContainer>
            {!readonlyMode && (
              <Checkbox
                label="Normtid bekræftet"
                checked={normTimeValidated}
                onChange={handleNormTimeValidatedChange}
                data-testid="normtid-checkbox"
                error={baseErrors?.normTimeValidated}
              />
            )}
          </RowItemContainer>
        </Row>
      )}

      <DateRow>
        {isWorkPermit ? (
          <>
            <DateTimePicker
              onChange={(date) => date?.isValid() && handleStartDateChange(date.toISOString())}
              value={earliestStartDate ? dayjs(earliestStartDate) : null}
              label="Start tidspunkt"
              disabled={readonlyMode || disableDates}
              slotProps={{
                textField: {
                  required: true,
                  error: baseErrors?.earliestStartDate,
                  helperText: 'Angiv tidspunktet for udkobling - forslag er baseret på hovedopgaven'
                }
              }}
            />
            <DateTimePicker
              onChange={(date) => date?.isValid() && handleDeadlineDateChange(date.toISOString())}
              value={dayjs(deadline)}
              label="Slut tidspunkt"
              disabled={readonlyMode || disableDates}
              minDateTime={earliestStartDate ? dayjs(earliestStartDate) : undefined}
              slotProps={{
                textField: {
                  required: true,
                  error: baseErrors?.deadline,
                  helperText: 'Angiv tidspunkt for indkobling - forslag er baseret på hovedopgaven'
                }
              }}
            />
          </>
        ) : (
          <>
            <DatePicker
              onDateChanged={(date) => handleStartDateChange(date.toISOString())}
              value={earliestStartDate ? formatDateString(earliestStartDate) : ''}
              label="Startdato"
              disabled={readonlyMode || disableDates}
              required
              error={baseErrors?.earliestStartDate}
            />
            <DatePicker
              onDateChanged={(date) => handleDeadlineDateChange(date.toISOString())}
              value={formatDateString(deadline)}
              label="Slutdato"
              disabled={readonlyMode || disableDates}
              required
              error={baseErrors?.deadline}
              earliestStartDate={earliestStartDate ? new Date(earliestStartDate) : undefined}
            />
          </>
        )}
        {!isMassCreation && !isWorkPermit && (
          <DatePicker
            onDateChanged={(date) => handleFollowUpDateChange(date.toISOString())}
            value={formatDateString((task as TaskCreationTask).followUpDate ?? '')}
            label="Opfølgningsdato"
            disabled={readonlyMode || disableDates}
          />
        )}
      </DateRow>

      <Row>
        <RowItemContainer>
          <DepartmentDropdown
            selectDepartment={selectDepartment}
            value={assignedToDepartmentId}
            disabled={readonlyMode}
            fullWidth
            error={baseErrors?.assignedToDepartmentId}
            label="Opgaven sendes til"
          />
        </RowItemContainer>

        <RowItemContainer>
          <AutoComplete
            id="project-number"
            onChange={(event, value, reason) =>
              handleProjectNumberChange(event, (value?.value.id ?? '') + value?.value.legalEntityId, reason)
            }
            getOptionLabel={(option) => option.label ?? ''}
            options={mappedProjectNumbers}
            value={selectedProjectNumber}
            renderInput={(params) => (
              <TextField
                {...params}
                label={'Projektnummer'}
                fullWidth
                required
                error={baseErrors?.projectNumber}
                dataTestId="task-create-project-select"
              />
            )}
            isOptionEqualToValue={(option, value) => option.value.id === value.value.id}
            disabled={readonlyMode || isLoadingProjectNumbers}
            fullWidth
            noOptionsText={'Intet projektnummer'}
            loading={true}
          />
          {'creationType' in task && task.creationType === 'relational' && (
            <ToolTip title="Hent projektnummer fra hovedopgaven">
              <IconButton onClick={getProjectNumberFromWorkTask}>
                {!isFetchingProjectNumber ? (
                  <DownloadIcon2 size="25px" color={n1LightTheme.palette.grey.black60} />
                ) : (
                  <CircularProgress size="25px" width="25px" margin="0" />
                )}
              </IconButton>
            </ToolTip>
          )}
        </RowItemContainer>
      </Row>

      {!isMassCreation && (
        <Row>
          <TextField
            multiline
            minRows={2}
            label="Opgavebeskrivelse"
            variant="outlined"
            onBlur={() => handleDescriptionBlur()}
            placeholder="Angiv en kort beskrivelse af opgaven"
            value={descriptionValue}
            onChange={(e) => setDescriptionValue(e.target.value)}
            required
            fullWidth
            dataTestId="task-description-textfield"
            error={baseErrors?.description}
          />
        </Row>
      )}
      <Row>
        <AutoComplete
          id="opgave-ansvarlig"
          onChange={(_, value, __) => handleWorkTaskResponsibleChange(value)}
          getOptionLabel={(option) => option.name ?? ''}
          options={users}
          value={{ email: workTaskResponsible?.email, name: workTaskResponsible?.name }}
          renderInput={(params) => (
            <TextField {...params} label={'Opgaveansvarlig'} fullWidth required error={baseErrors?.projectNumber} />
          )}
          disabled={readonlyMode || isLoadingProjectNumbers}
          fullWidth
        />
      </Row>
      {task.taskType?.isPreparationTask && !isMassCreation && (
        <Row>
          <RowItemContainer>
            <TextField
              type="number"
              fullWidth
              required={taskTypeHasDayWindow}
              label="Min. dage før hovedopgave"
              value={(task as TaskCreationTask).minTimeBeforeMainTaskDays ?? ''}
              onChange={(e) => handlePreparationDaysChange('minTimeBeforeMainTaskDays', e.target.value)}
              error={baseErrors?.minTimeBeforeMainTaskDays}
              helperText={getPreparationMinHelperText()}
              inputProps={{
                min: task.taskType.minTimeBeforeMainTaskDays ?? 0,
                max: task.taskType.maxTimeBeforeMainTaskDays ?? undefined,
                step: 1
              }}
            />
          </RowItemContainer>
          <RowItemContainer>
            <TextField
              type="number"
              fullWidth
              required={taskTypeHasDayWindow}
              label="Maks. dage før hovedopgave"
              value={(task as TaskCreationTask).maxTimeBeforeMainTaskDays ?? ''}
              onChange={(e) => handlePreparationDaysChange('maxTimeBeforeMainTaskDays', e.target.value)}
              error={baseErrors?.maxTimeBeforeMainTaskDays}
              helperText={getPreparationMaxHelperText()}
              inputProps={{
                min: task.taskType.minTimeBeforeMainTaskDays ?? 0,
                max: task.taskType.maxTimeBeforeMainTaskDays ?? undefined,
                step: 1
              }}
            />
          </RowItemContainer>
        </Row>
      )}
    </SectionContent>
  );
};

const DateRow = styled.div`
  display: flex;
  column-gap: 24px;
  align-items: center;
  flex-wrap: wrap;
  row-gap: 16px;

  & > * {
    flex: 1 1 0;
    min-width: 150px;
  }
`;

const RowItemContainer = styled.div`
  flex: 1 1 0;
  width: 0;
  display: flex;
  align-items: center;
`;

export default DetailsSection;
